import { BreadCrumbItem, BreadCrumbs } from '@eventbrite/breadcrumbs-infra';
import {
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_REFINEMENT_TO_CATEGORY,
    FORMATTED_SUBCATEGORY_MAP,
    SUBCATEGORY_REFINEMENT_TO_KEY,
} from '@eventbrite/categories';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { Link } from 'react-router-dom';
import { useRouteContext } from '../../context';
import { useLocation } from '../../hooks/queries/location';
import './CategoryBrowseBreadCrumb.scss';

interface CategoryBrowseBreadcrumbItem {
    clientSideLink?: boolean;
}

interface IGenerateBreadCrumbs {
    category?: string;
    subcategory?: string;
    locationSlug?: string;
    currentPlace?: string;
    placeType?: string;
    country?: string;
    countrySlug?: string;
    city?: string;
    citySlug?: string;
    borough?: string;
    boroughSlug?: string;
    isOnline?: boolean;
    showThingsToDoBreadcrumbs: boolean;
    languageSubdirectory?: string;
}
export const generateBreadCrumbs = ({
    category,
    subcategory,
    locationSlug,
    currentPlace,
    placeType = '',
    country,
    countrySlug,
    city,
    citySlug,
    borough,
    boroughSlug,
    isOnline,
    showThingsToDoBreadcrumbs,
    languageSubdirectory,
}: IGenerateBreadCrumbs): BreadCrumbItem<CategoryBrowseBreadcrumbItem>[] => {
    let items: BreadCrumbItem<CategoryBrowseBreadcrumbItem>[] = [
        {
            value: `${languageSubdirectory}/`,
            display: gettext('Home'),
            clientSideLink: true,
        },
    ];

    if (showThingsToDoBreadcrumbs) {
        if (
            country &&
            ['neighbourhood', 'borough', 'locality', 'region'].includes(
                placeType,
            )
        ) {
            items.push({
                value: `${languageSubdirectory}/ttd/${countrySlug}/`,
                display: country,
            });
        }

        if (city && ['neighbourhood', 'borough'].includes(placeType)) {
            items.push({
                value: `${languageSubdirectory}/ttd/${citySlug}/`,
                display: city,
            });
        }

        if (borough && placeType === 'neighbourhood') {
            items.push({
                value: `${languageSubdirectory}/ttd/${boroughSlug}/`,
                display: borough,
            });
        }
    }

    if (currentPlace && category) {
        const categoryId = EVENTBRITE_REFINEMENT_TO_CATEGORY[category];

        items = [
            ...items,
            ...(showThingsToDoBreadcrumbs
                ? [
                      {
                          value: `${languageSubdirectory}/ttd/${locationSlug}/`,
                          display: currentPlace,
                      },
                  ]
                : []),
            {
                value: `${languageSubdirectory}/d/${locationSlug}/events/`,
                display: showThingsToDoBreadcrumbs
                    ? isOnline
                        ? 'Online Events'
                        : `Events in ${currentPlace}`
                    : currentPlace,
            },
            {
                value: `${languageSubdirectory}/b/${locationSlug}/${category}/`,
                display: EVENTBRITE_CATEGORY_MAPPING[categoryId],
                clientSideLink: true,
            },
        ];

        if (subcategory) {
            const subcategoryId =
                SUBCATEGORY_REFINEMENT_TO_KEY[categoryId][subcategory];
            const { title } = FORMATTED_SUBCATEGORY_MAP[subcategoryId];

            items = [
                ...items,
                {
                    value: `${languageSubdirectory}/b/${locationSlug}/${category}/${subcategory}/`,
                    display: title,
                    clientSideLink: true,
                },
            ];
        }
    }

    return items;
};

const useCreateBreadCrumbItems = (languageSubdirectory: string) => {
    const { data: location } = useLocation();
    const routeContext = useRouteContext();

    const featureFlags = routeContext.featureFlags;
    const category = routeContext.match?.params.category || 'music';
    const subcategory = routeContext.match?.params.subcategory;
    const currentPlace = location?.currentPlace;
    const locationSlug = location?.slug;
    const placeType = location?.placeType;
    const country = location?.country;
    const countrySlug = location?.countrySlug;
    const city = location?.city;
    const citySlug = location?.citySlug;
    const borough = location?.borough;
    const boroughSlug = location?.boroughSlug;
    const isOnline = location?.isOnline;
    const showThingsToDoBreadcrumbs =
        featureFlags?.enableThingsToDoPhase2 || false;

    const [breadcrumbs, setBreadCrumbs] = React.useState<
        BreadCrumbItem<CategoryBrowseBreadcrumbItem>[]
    >(() =>
        generateBreadCrumbs({
            category,
            subcategory,
            currentPlace,
            locationSlug,
            placeType,
            country,
            countrySlug,
            city,
            citySlug,
            borough,
            boroughSlug,
            isOnline,
            showThingsToDoBreadcrumbs,
            languageSubdirectory,
        }),
    );

    React.useEffect(() => {
        const updatedItems = generateBreadCrumbs({
            category,
            subcategory,
            currentPlace,
            locationSlug,
            placeType,
            country,
            countrySlug,
            city,
            citySlug,
            borough,
            boroughSlug,
            isOnline,
            showThingsToDoBreadcrumbs,
            languageSubdirectory,
        });

        setBreadCrumbs(updatedItems);
    }, [
        category,
        subcategory,
        locationSlug,
        currentPlace,
        placeType,
        country,
        countrySlug,
        city,
        citySlug,
        borough,
        boroughSlug,
        isOnline,
        showThingsToDoBreadcrumbs,
        languageSubdirectory,
    ]);

    return breadcrumbs;
};

export const CategoryBrowseBreadCrumb: React.FunctionComponent<{
    languageSubdirectory: string;
}> = ({ languageSubdirectory }) => {
    const items = useCreateBreadCrumbItems(languageSubdirectory);

    return (
        <BreadCrumbs
            items={items}
            containerClasses="eds-text-bm category-browse-breadcrumb"
            shouldIncludeMicroData={true}
            separator={
                <span style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                    /
                </span>
            }
            ariaLabel={gettext('breadcrumbs')}
        >
            {(item) =>
                item.clientSideLink ? (
                    <Link to={item.value} {...item.valueMicrodataProps}>
                        {item.displayWithMicroData}
                    </Link>
                ) : (
                    <a href={item.value} {...item.valueMicrodataProps}>
                        {item.displayWithMicroData}
                    </a>
                )
            }
        </BreadCrumbs>
    );
};
